'use strict';

import axios from 'axios';
import { updateSortUrls } from '@jspages/search/sortingButtons';
import { startSpinner, stopSpinner } from '@jscomponents/spinner';
import { listenDOMEvent, createElement } from '@jspartials/dom-api-utils';
import {
  gtmLaunchClickButtonShowMoreResults,
  gtmListenProductTiles
} from '@jspartials/gtm-utils';
import { customEvent } from '@jspartials/general-utils';
import { listenImagesCarousel } from '@jspages/search/imagesCarouselTiles';

// Selectors
const selector = {
  wrapper: {
    gridFooter: '.js-grid-footer'
  },
  button: {
    showMore: '.js-button-show-more'
  }
};

// Variables
const eventReloadVtoButtons = new CustomEvent(customEvent.reloadVtoButtons);
const eventListenProductTiles = new CustomEvent(customEvent.listenProductTiles);
const eventInitProductTilesAnimation = new CustomEvent(
  customEvent.initProductTilesAnimation
);
let infiniteScrollEnabled = false;
let observer;

// Functions

/**
 * Handle button show more visibility
 * @param {Array} entries - Entries to observe
 * @returns {void}
 */
const handleButtonShowMoreVisibility = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting && infiniteScrollEnabled) {
      loadMoreProducts(entry.target);
    }
  });
};

/**
 * Observe button show more
 * @returns {void}
 */
const observeButtonShowMore = () => {
  const buttonShowMore =
    document.querySelector(selector.button.showMore) || null;

  if (buttonShowMore) {
    observer = new IntersectionObserver(handleButtonShowMoreVisibility, {
      root: null,
      threshold: 0.5
    });

    observer.observe(buttonShowMore);
  }
};

/**
 * Paint new content in grid footer
 * @param {String} content - Content to paint
 * @returns {void}
 */
const paintNewContent = (content) => {
  const gridFooterElement =
    document.querySelector(selector.wrapper.gridFooter) || null;

  if (gridFooterElement) {
    const newElement = createElement({
      tag: 'div',
      content: content
    });

    gridFooterElement.replaceWith(...newElement.firstChild.childNodes);
  }
};

/**
 * Load more products
 * @param {HTMLElement} buttonShowMore - Button show more
 * @returns {void}
 */
const loadMoreProducts = (buttonShowMore) => {
  const url = buttonShowMore?.dataset?.url || null;

  if (url) {
    startSpinner();

    axios
      .get(url)
      .then((response) => {
        paintNewContent(response.data);
        updateSortUrls(response.data);

        infiniteScrollEnabled = true;
        listenImagesCarousel();
        observeButtonShowMore();
        listenButtonShowMore();
        gtmClickButtonShowMoreResults();
        gtmListenProductTiles();
        document.dispatchEvent(eventListenProductTiles);
        document.dispatchEvent(eventReloadVtoButtons);
        document.dispatchEvent(eventInitProductTilesAnimation);
      })
      .catch((error) => {
        console.error(error);
      })
      .then(() => {
        stopSpinner();
      });
  }
};

/**
 * Dispatch GTM event when user clicks on button "Show more results"
 * @returns {void}
 */
const gtmClickButtonShowMoreResults = () => {
  const gridFooterElement =
    document.querySelector(selector.wrapper.gridFooter) || null;
  const pageNumber = gridFooterElement?.dataset?.pageNumber || 0;

  gtmLaunchClickButtonShowMoreResults(pageNumber);
};

/**
 * Listen button show more event click
 * @returns {void}
 */
const listenButtonShowMore = () => {
  const buttonShowMore =
    document.querySelector(selector.button.showMore) || null;

  if (buttonShowMore) {
    listenDOMEvent(buttonShowMore, 'click', (event) => {
      event.stopPropagation();
      event.preventDefault();

      loadMoreProducts(buttonShowMore);
    });
  }
};

export { listenButtonShowMore };
